import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { EXAMPLE_GROUP_ID, PlaceholderMemberService } from '@splitfiesta/core';
import {
  MAX_PLACEHOLDERS_PER_GROUP,
  PLACEHOLDER_LIMIT_ERROR_KEY,
} from '@splitfiesta/shared';

import { useCreatePlaceholderMutation } from '@/hooks/queries/useGroups';
import type { SavedSpotMember, SavedSpotRole } from '@/hooks/useExpenseForm';

import { handleError, isTranslatableError } from '@/utils/errorHandler';

interface Args {
  groupId: string;
  isOnline: boolean;
  /** The form's members; the saved spots among them count against the cap. */
  members: ReadonlyArray<{ isPlaceholder?: boolean }>;
  holdSplitOnMemberChange: () => void;
  placeSavedSpot: (member: SavedSpotMember, role: SavedSpotRole) => void;
}

export interface SaveSpotFromForm {
  /** Online and a real group: the pickers render the control at all. */
  offered: boolean;
  /** Room for one more spot; false only hides the closed control. */
  available: boolean;
  /** Rejects on failure, so the field keeps the name and shows the error. */
  createAndPlace: (name: string, role: SavedSpotRole) => Promise<void>;
}

/** "Save a spot" from the expense form's pickers: one create, one placement. */
export function useSaveSpotFromForm(args: Args): SaveSpotFromForm {
  const { groupId, isOnline, members } = args;
  const argsRef = useRef(args);
  useLayoutEffect(() => {
    argsRef.current = args;
  });
  // The roster can lag the server; the API's refusal is the real cap, and it
  // belongs to the group that refused.
  const [limitHitFor, setLimitHitFor] = useState<string | null>(null);
  const { mutateAsync } = useCreatePlaceholderMutation();

  const createAndPlace = useCallback(
    async (name: string, role: SavedSpotRole) => {
      const startedIn = argsRef.current.groupId;
      argsRef.current.holdSplitOnMemberChange();
      try {
        const member = await mutateAsync({
          groupId: startedIn,
          name: PlaceholderMemberService.validateName(name).trimmed,
        });
        // The form moved to another group: the spot stays in its own group.
        if (argsRef.current.groupId !== startedIn) return;
        // The latest form callbacks: the amount may have moved during the request.
        argsRef.current.placeSavedSpot(
          { userId: member.userId, name: member.name },
          role
        );
      } catch (err) {
        const state = handleError(err);
        if (
          isTranslatableError(state) &&
          state.key === PLACEHOLDER_LIMIT_ERROR_KEY
        ) {
          setLimitHitFor(startedIn);
        }
        throw err;
      }
    },
    [mutateAsync]
  );

  const placeholderCount = members.filter(m => m.isPlaceholder).length;
  return {
    offered: isOnline && groupId !== EXAMPLE_GROUP_ID,
    available:
      limitHitFor !== groupId && placeholderCount < MAX_PLACEHOLDERS_PER_GROUP,
    createAndPlace,
  };
}
